import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import Axios from 'axios';
import CustomerForm from './CustomerForm';
import PaymentModal from './PaymentModal';
import PlanTable from './PlanTable';
import PaymentHistory from './PaymentHistory';
import { addToLocalStorage } from './utils';
import { addToInventory, addToOrganizationMembers, getOrganizationByName, getUserByEmail } from './graphql';
import { BillsbyUrl } from '../../config/properties';
import { SubsContext } from './SubscriptionInfo';
import { altPlanData, getPrice } from './plans';

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

function validateForm(data) {

  const errors = {};
  const validationRules = {
    addressLine1: "Address Line 1 is required.",
    city: "City is required.",
    state: "State is required.",
    postCode: {
      required: "Postcode is required.",
      format: "Postcode must be exactly 5 digits or 9 digits."
    },
    country: "Country must be US, USA, or United States."
  };

  Object.keys(validationRules).forEach(field => {
    if (field === "postCode") {
      if (!data[field]) {
        errors[field] = validationRules[field].required;
      } else if (!/^(\d{5}|\d{5}-?\d{4})$/.test(data[field])) {
        errors[field] = validationRules[field].format;
      }
    } else if (field === "country") {
      if (data[field]) {
        if (data[field] !== 'US' && data[field] !== 'USA') {
          errors[field] = validationRules[field];
        }
      }
    } else if (!data[field]) {
      errors[field] = validationRules[field];
    }
  });

  return Object.keys(errors).length > 0 ? errors : "Validation successful!";
}

// eslint-disable-next-line react/prop-types
const SubscriptionPlans = ({ auth, companyInfo }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState('');
  const [addressError, setAddressErrors] = useState({});

  const {
    setSubscriptionLevel,
    billsbyCustomerId,
    setBillsbyCustomerId,
    subscription,
    setBillsbyCardInfo,
    getBillsbyCardInfo,
    updateSubscription,
    plans,
    setPreviousPlan,
  } = useContext(SubsContext);

  const poc = companyInfo ? (companyInfo.pointOfContact ? companyInfo.pointOfContact.split(' ') : [' ', ' ']) : [' ', ' '];
  const zPoc = poc.length;
  const [customerInfo, setCustomerInfo] = companyInfo ? useState({
    ...companyInfo,
    companyName: companyInfo.name,
    addressLine1: companyInfo.address1,
    postCode: companyInfo.zipCode,
    country: companyInfo.cntry,
    firstName: zPoc < 2 ? '' : poc.slice(0, zPoc - 1).join(' '),
    lastName: zPoc ? poc[zPoc - 1] : '',
  }) :
    useState({
      email: '',
      companyName: '',
      firstName: '',
      lastName: '',
      addressLine1: '',
      city: '',
      state: '',
      postCode: '',
      country: 'USA',
      subscriptionLevel: ''
    });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getUserDetails = async (email) => {
    console.log(email)
    const { getUserByEmailOrganization } = await getUserByEmail(email);

    if (getUserByEmailOrganization) {
      const org = getOrganizationByName(getUserByEmailOrganization.organizationName);
      console.log("Organization:", org);
      const name = getUserByEmailOrganization.name.split(" ");
      const zName = name.length;
      setCustomerInfo(prev => ({
        ...prev,
        companyName: getUserByEmailOrganization.organizationName,
        email,
        firstName: zName < 2 ? '' : name.slice(0, zName - 1).join(' '),
        lastName: zName ? name[zName - 1] : '',
      }));

    }
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailFromUrl = urlParams.get('email');
    getUserDetails(emailFromUrl);

  }, []);

  const handleSubscribe = async (planDetails, previousPlan = '') => {
    if (planDetails) {
      console.log("inside method handleSubscribe!")
      const newSelectedPlan = {
        ...planDetails,
        userTypeName: planDetails.userType || "Public",
        previousPlan
      }
      setSelectedPlan(newSelectedPlan);
      if (!billsbyCustomerId && planDetails.name === 'None' /* planDetails.price === 'FREE' */) {
        if (!companyInfo) {
          await addToInventory({
            email: customerInfo.email,
            billsbyCustomerId: '',
            subscriptionLevel: planDetails.name,
            previousPlan,
          });
          const subscriptionData = {
            email: customerInfo.email,
            subscriptionUniqueId: '',
            billsbyCustomerId: '',
            subscriptionLevel: planDetails.name,
            previousPlan,
          }
          addToLocalStorage("userSubscriptionDetails", subscriptionData);
        } else {
          await addToOrganizationMembers({
            email: customerInfo.email,
            billsbyCustomerId: '',
            subscriptionLevel: planDetails.name,
            previousPlan,
          });
        }
        setSubscriptionLevel(planDetails.name);
        window.scroll(0, 0);
      } else {
        const validationResult = validateForm(customerInfo);
        if (typeof validationResult === 'object' && validationResult !== null) {
          setAddressErrors(validationResult);
        } else {
          setAddressErrors({});
          setShowPaymentModal(true);
        }
      }
    } else {
      setShowPaymentModal(true);
      setAddressErrors({});
    }
  };

  const handleCustomerSubmit = async (customerData) => {
    setCustomerInfo(customerData);
  };

  const createSubscription = async (pmData, planDetails) => {
    // TODO - For now, credit card information is stored in nBillsbyCustomerId: "billsbyCustomerId last4Digits expiryMonth expiryYear cordType"
    let nBillsbyCustomerId = getBillsbyCardInfo();
    try {
      setIsLoading(true);
      console.log('Creating subscription with plan details:', planDetails);

      // Prepare the customer data with subscription:
      if (!billsbyCustomerId) {
        const customerData = {
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          email: customerInfo.email,
          companyName: customerInfo.companyName,
          cycleId: Number(planDetails.cycleId),
          // Use the group count as units
          units: planDetails.groupCount || 1,
          address: {
            addressLine1: customerInfo.addressLine1,
            city: customerInfo.city,
            state: customerInfo.state,
            country: customerInfo.country || 'USA',
            postCode: customerInfo.postCode
          },
          cardDetails: {
            FullName: pmData.full_name,
            PaymentCardToken: pmData.token,
            Last4Digits: pmData.last_four_digits,
            ExpiryMonth: pmData.month,
            ExpiryYear: pmData.year,
            CardType: pmData.card_type,
          },
          marketingConsent: false,
          customFields: {
            companyName: customerInfo.companyName
          }
        };

        console.log('Sending customer data to Billsby:', JSON.stringify(customerData, null, 2));

        const response = await Axios.post(`${BillsbyUrl}/createCustomer`, customerData);
        console.log('Billsby API Response:', response.data);
        const customerUniqueId = response.data.customerUniqueId;
        // TODO - For now, credit card information is stored in nBillsbyCustomerId: "billsbyCustomerId last4Digits expiryMonth expiryYear cordType"
        nBillsbyCustomerId = setBillsbyCardInfo(customerUniqueId, pmData.last_four_digits, pmData.month, pmData.year, pmData.card_type);
        setBillsbyCustomerId(customerUniqueId);
        if (auth) {
          await addToOrganizationMembers({
            email: customerInfo.email,
            billsbyCustomerId: nBillsbyCustomerId,
            subscriptionLevel: planDetails.name,
            previousPlan: planDetails.previousPlan,
          });
        } else {
          await addToInventory({
            email: customerInfo.email,
            billsbyCustomerId: nBillsbyCustomerId,
            subscriptionLevel: planDetails.name,
            previousPlan: planDetails.previousPlan,
          });
        }
        const subscriptionData = {
          email: customerInfo.email,
          subscriptionUniqueId: response.data.subscriptionUniqueId,
          billsbyCustomerId: nBillsbyCustomerId,
          subscriptionLevel: planDetails.name,
          previousPlan: planDetails.previousPlan,
        }
        addToLocalStorage("userSubscriptionDetails", subscriptionData);
        setShowPaymentModal(false);
        window.scroll(0, 0);
      } else {
        // Update Credit Card:
        if (pmData) {
          const cardInfo = {
            FullName: pmData.full_name,
            PaymentCardToken: pmData.token,
            Last4Digits: pmData.last_four_digits,
            ExpiryMonth: pmData.month,
            ExpiryYear: pmData.year,
            CardType: pmData.card_type,
          };
          // Update payment card token:
          await Axios.put(`${BillsbyUrl}/updateCard/${billsbyCustomerId}`, cardInfo);
          // TODO - For now, credit card information is stored in nBillsbyCustomerId: "billsbyCustomerId last4Digits expiryMonth expiryYear cordType"
          nBillsbyCustomerId = setBillsbyCardInfo(billsbyCustomerId, pmData.last_four_digits, pmData.month, pmData.year, pmData.card_type);
        }
        // Change plan:
        if (planDetails) {
          let now;
          if (subscription.status === 'InTrial') {
            now = false;
          } else if (subscription.status === 'Suspended' || subscription.status === 'Cancelled') {
            // For cancelled or suspended subscriptions, we want to reactivate immediately
            now = true;
          } else {
            const currPrice = getPrice(subscription.planId, subscription.cycleId, plans);
            const newPrice = getPrice(Number(planDetails.planId), Number(planDetails.cycleId), plans);
            now = newPrice >= currPrice;
          }
          console.log("Immediate plan change:", now);
          const planData = {
            planId: Number(planDetails.planId),
            cycleId: Number(planDetails.cycleId),
            customerUniqueId: billsbyCustomerId,
            planChangeType: now ? 1 : 2,
            issueRefund: now,
          };

          // Make sure we have a subscription ID before proceeding
          if (!subscription || !subscription.subscriptionUniqueId) {
            throw new Error('Missing subscription ID - cannot update plan');
          }

          console.log(`Update Plan for subscription ${subscription.subscriptionUniqueId}:`, planData);
          try {
            await Axios.put(`${BillsbyUrl}/changePlan/${subscription.subscriptionUniqueId}`, planData);

            // Show success message for resubscribe case
            if (subscription.status === 'Suspended' || subscription.status === 'Cancelled') {
              setError(""); // Clear any previous errors
              // We don't need to display a success message as the UI will update automatically
            }
          } catch (err) {
            try {
              console.log("Use alternate cycle for a plan");
              await Axios.put(`${BillsbyUrl}/changePlan/${subscription.subscriptionUniqueId}`, altPlanData(planData, plans));

              // Show success message for resubscribe case with alternate cycle
              if (subscription.status === 'Suspended' || subscription.status === 'Cancelled') {
                setError(""); // Clear any previous errors
              }
            } catch (err1) {
              console.log("Failed to find alternate cycle for a plan");
              throw err1;
            }
          }
          await addToOrganizationMembers({
            email: customerInfo.email,
            billsbyCustomerId: nBillsbyCustomerId,
            subscriptionLevel: planDetails.name,
            previousPlan: planDetails.previousPlan,
          });
        } else if (pmData) {
          await addToOrganizationMembers({
            email: customerInfo.email,
            billsbyCustomerId: nBillsbyCustomerId,
            subscriptionLevel: subscription.planName.split(' - ')[1],
            previousPlan: planDetails.previousPlan,
          });
        }
        window.scroll(0, 0);
        setShowPaymentModal(false);
        setAddressErrors({});
        setError("");
      }
      updateSubscription();
      setPreviousPlan(planDetails.previousPlan);
    } catch (err) {
      console.error("Plan subscription:", err);
      let errorMessage = 'Failed to create subscription';
      if (err.response) {
        if (err.response.data && err.response.data.errors) {
          errorMessage = err.response.data.errors[0].description;
        } else if (err.response.statusText) {
          errorMessage = err.response.statusText;
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSubmit = (token) => {
    // Use the token to create the subscription
    createSubscription(token, selectedPlan);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    if (Object.keys(addressError).length !== 0) {
      const validationResult = validateForm(customerInfo);
      if (typeof validationResult === 'object' && validationResult !== null) {
        setAddressErrors(validationResult);
      } else {
        setAddressErrors({});
      }
    }
  }, [customerInfo, addressError]);

  return (
    <Container>
      <CustomerForm
        customerInfo={{
          ...customerInfo,
          industryAssociations: customerInfo.industryAssociations || []
        }}
        onInputChange={handleInputChange}
        error={error}
        addressError={addressError}
        onSubmit={handleCustomerSubmit}
      />

      <PlanTable
        onSubscribe={handleSubscribe}
        isLoading={isLoading}
      />

      <PaymentModal
        visible={showPaymentModal}
        isLoading={isLoading}
        error={error}
        onSubmit={handlePaymentSubmit}
        onClose={() => setShowPaymentModal(false)}
        customerInfo={customerInfo}
        selectedPlan={selectedPlan}
      />

      {subscription && billsbyCustomerId && (
        <PaymentHistory />
      )}
    </Container>
  );
};

export default SubscriptionPlans;



// WEBPACK FOOTER //
// ./src/components/Billsby/SubscriptionPlan.jsx